import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { CostCenter } from 'src/shared/models/cost-center.model';
import { CostCenterConfigurationService } from 'src/shared/services/cost-center-configuration.service';
import { InpatientConfiguration } from 'src/shared/models/inpatient-configuration.model';
import { AncillaryConfiguration } from 'src/shared/models/ancillary-configuration.model';
import { CostCenterService } from 'src/shared/services/cost-center.service';
import { Subscription } from 'rxjs';
import { Client } from 'src/shared/models/client.model';
import { ClientService } from 'src/shared/services/client.service';

@Component({
    selector: 'smt-details-tab',
    templateUrl: './details-tab.component.html',
    styleUrls: ['./details-tab.component.scss']
})
export class DetailsTabComponent implements OnInit, OnDestroy {
    @Input() costCenter: CostCenter;
    @Input() readOnly: boolean;
 
    saveSubscription: Subscription;
    selectedSubscription: Subscription;
    inpatientNursingAccess: boolean;
    nursingProceduralAncillaryAccess: boolean;
    client: Client;
    isCostCenterTypeDisabled: boolean;

    readonly errorString: string = "Please correct all errors.";

    constructor(
        private _costCenterService: CostCenterService,
        private _costCenterConfigurationService: CostCenterConfigurationService,
        private _clientService: ClientService) {

    }

    get isInpatient(): boolean {
        return this.costCenter && this.costCenter.costCenterType === 'inpatient';
    }

    get isAncillary(): boolean {
        return this.costCenter && this.costCenter.costCenterType === 'ancillary';
    }

    get hasSelectedConfiguration(): boolean {
        return (this.isInpatient && this.costCenter.inpatientConfiguration && !!this.costCenter.inpatientConfiguration.id)
            || (this.isAncillary && this.costCenter.ancillaryConfiguration && !!this.costCenter.ancillaryConfiguration.id);
    }

    ngOnInit() {
        this.checkFeatureAccess().then(() => {
            this.changeType();
        });

        this.selectedSubscription = this._costCenterConfigurationService.selected$.subscribe((costCenter: CostCenter) => {
            this.costCenter = costCenter;
            this.checkFeatureAccess();
        });

        this.saveSubscription = this._costCenterConfigurationService.save$.subscribe((tab: string) => {
            if (tab === 'details') {
                this.save().then(() => {
                    this._costCenterConfigurationService.announceAdminListReload(false);
                });
            }
        });
    }

    ngOnDestroy() {
        this.saveSubscription.unsubscribe();
        this.selectedSubscription.unsubscribe();
    }

    changeType() {
        if (this.isAncillary && !this.costCenter.ancillaryConfiguration) {
            this.costCenter.ancillaryConfiguration = new AncillaryConfiguration();
            this.costCenter.ancillaryConfiguration.roomTypes = [];
            this.costCenter.ancillaryConfiguration.acuityCategories = [];
        } 
        else if (this.isInpatient && !this.costCenter.inpatientConfiguration) {
            this.costCenter.inpatientConfiguration = new InpatientConfiguration();
            this.costCenter.inpatientConfiguration.patientRatios = [];
        }
    }

    async save() {
        if (!this.costCenter) {
            return;
        }

        this._costCenterConfigurationService.announceSaving(true);

        switch (this.costCenter.costCenterType) {
            case 'ancillary':
                this.costCenter.ancillaryConfiguration.costCenterId = this.costCenter.id;
                await this._costCenterService.setAncillaryConfiguration(this.costCenter.ancillaryConfiguration);
                break;
            case 'inpatient':
                this.costCenter.inpatientConfiguration = this.costCenter.inpatientConfiguration;
                this.costCenter.inpatientConfiguration.costCenterId = this.costCenter.id;
                await this._costCenterService.setInpatientConfiguration(this.costCenter.inpatientConfiguration);
                break;
        }

        // Refresh the cost center to get Acuity Categories that have IDs
        this.costCenter = await this._costCenterService.getCostCenter(this.costCenter.id);

        this._costCenterConfigurationService.announceSaving(false);
        this._costCenterConfigurationService.setSelected(this.costCenter);
        this._costCenterConfigurationService.announceFormIsDirty(false);
        this._costCenterConfigurationService.clearError();        
    }

    setIsCostCenterTypeDisabled() {
        this.isCostCenterTypeDisabled = !(this.inpatientNursingAccess && this.nursingProceduralAncillaryAccess);
    }

    setCostCenterTypeForFeature() {
        let costCenterType = null;
        if (this.inpatientNursingAccess && !this.nursingProceduralAncillaryAccess) {
            costCenterType = 'inpatient';
        }
        else if (!this.inpatientNursingAccess && this.nursingProceduralAncillaryAccess) {
            costCenterType = 'ancillary';
        }
        else if (this.inpatientNursingAccess && this.nursingProceduralAncillaryAccess) {
            costCenterType =null;
        }
        if (this.costCenter.costCenterType == null) {
            this.costCenter.costCenterType = costCenterType;
        }
    }

    async checkFeatureAccess() {
        this._costCenterConfigurationService.announceAdminActionLoading(true);
        let result = this._clientService.GetClientById();
        return await result.then((data) => {
            if (data) {
                this.client = data;
                this.inpatientNursingAccess = this.client.inpatientNursingAccess;
                this.nursingProceduralAncillaryAccess = this.client.nursingProceduralAncillaryAccess;
                this.setCostCenterTypeForFeature();
                this.setIsCostCenterTypeDisabled();
            }
            this._costCenterConfigurationService.announceAdminActionLoading(false);
        });
    }
}
